import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams, useNavigate, useNavigationType, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  SortAsc,
  Grid3X3,
  List,
  Check,
  Sparkles,
  PackageSearch,
  SlidersHorizontal,
  Layers3
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  fetchProducts,
  setSelectedCategory,
  setSortBy,
  fetchStoreDiscounts, // Add this

  selectFilteredProducts,
  selectAllProducts,
  setDisplayedCount,
  setScrollPosition
} from '../store/slices/productsSlice';
import ProductCard from '../components/product/ProductCard';
import Button from '../components/common/Button';
import { PageLoader } from '../components/common/LoadingSpinner';
import { getProductsRoute, normalizeProductCategory } from '../utils/internalRouter';

const Products = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const location = useLocation();
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { loading, selectedCategory, sortBy, displayedCount, scrollPosition } = useSelector((state) => state.products);
  const filteredProducts = useSelector(selectFilteredProducts);


  const [viewMode, setViewMode] = useState('grid');

  const [sortOpen, setSortOpen] = useState(false);
  // displayedCount moved to Redux
  const [loadingMore, setLoadingMore] = useState(false);

  const PRODUCTS_PER_PAGE = 24;

  const allProducts = useSelector(selectAllProducts);

  // Scroll Restoration Logic
  useLayoutEffect(() => {
    const restoreScroll = () => {
      if (window.history.scrollRestoration) {
        window.history.scrollRestoration = 'manual';
      }

      if (navType === 'POP' && scrollPosition > 0) {
        // Attempt to restore scroll multiple times to handle dynamic content loading
        let attempts = 0;
        const maxAttempts = 120; // Increased to ~2 seconds to handle deep lists/slow renders

        const attemptScroll = () => {
          // Force scroll
          window.scrollTo({
            top: scrollPosition,
            behavior: 'instant'
          });

          // Check if we made it (tolerance of 5px)
          const currentScroll = window.scrollY;
          const diff = Math.abs(currentScroll - scrollPosition);
          const hasNotReached = diff > 10;

          // Also check if page is tall enough yet
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          const isPageShort = maxScroll < scrollPosition;

          if ((hasNotReached || isPageShort) && attempts < maxAttempts) {
            attempts++;
            requestAnimationFrame(attemptScroll);
          }
        };

        requestAnimationFrame(attemptScroll);
      }
    };

    restoreScroll();

    return () => {
      // Re-enable native restoration when leaving (optional, but good citizenship)
      if (window.history.scrollRestoration) {
        window.history.scrollRestoration = 'auto';
      }
    };
  }, [navType, scrollPosition]); // Added scrollPosition to deps

  // Save Scroll Position on Unmount
  useEffect(() => {
    return () => {
      dispatch(setScrollPosition(window.scrollY));
    };
  }, [dispatch]);

  useEffect(() => {
    // Only fetch if data is missing to allow smooth back navigation
    if (!allProducts || allProducts.length === 0) {
      dispatch(fetchProducts());
    }
    dispatch(fetchStoreDiscounts()); // Fetch discounts
  }, [dispatch, allProducts]);

  useEffect(() => {
    const pathAlias = location.pathname.replace(/^\/+|\/+$/g, '').toLowerCase();
    const aliasCategory = ['premium', 'exclusive', 'exclusives', 'bundle', 'bundles', 'addon', 'addons', 'free', 'free-scripts', 'other', 'others'].includes(pathAlias)
      ? normalizeProductCategory(pathAlias)
      : null;
    const nextCategory = normalizeProductCategory(category || aliasCategory || 'all');

    if (nextCategory && nextCategory !== 'all') {
      dispatch(setSelectedCategory(nextCategory));
    } else {
      dispatch(setSelectedCategory('all'));
    }
  }, [category, location.pathname, dispatch]);

  useEffect(() => {
    const sort = searchParams.get('sort');
    if (sort) {
      dispatch(setSortBy(sort));
    }
  }, [searchParams, dispatch]);

  const allLabel = t('common.all', 'All');
  
  const categories = [
    { value: 'all', label: allLabel === 'common.all' ? 'All' : allLabel },
    { value: 'premium', label: t('common.premium') },
    { value: 'exclusive', label: t('common.exclusive', 'Exclusive') },
    { value: 'bundle', label: t('common.bundle') },
    { value: 'addon', label: t('common.addons', 'Addons') },
    { value: 'free', label: t('common.free_scripts', 'Free Scripts') },
    { value: 'other', label: t('common.others') },
  ];

  const sortOptions = [
    { value: 'featured', label: t('common.featured') },
    { value: 'price-low', label: t('common.price_low_high') },
    { value: 'price-high', label: t('common.price_high_low') },
    { value: 'newest', label: t('common.newest_first') },
    { value: 'name', label: t('common.name_az') },
  ];

  const handleCategoryChange = (newCategory) => {
    navigate(getProductsRoute(newCategory));
    dispatch(setDisplayedCount(PRODUCTS_PER_PAGE));
  };

  const handleSortChange = (newSort) => {
    dispatch(setSortBy(newSort));
    setSortOpen(false);
    setSearchParams({ sort: newSort });
    dispatch(setDisplayedCount(PRODUCTS_PER_PAGE));
  };

  const handleLoadMore = async () => {
    setLoadingMore(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    dispatch(setDisplayedCount(displayedCount + PRODUCTS_PER_PAGE));
    setLoadingMore(false);
  };

  const displayedProducts = filteredProducts.slice(0, displayedCount);
  const hasMoreProducts = filteredProducts.length > displayedCount;
  const categoryCounts = React.useMemo(() => {
    const counts = { all: allProducts.length, premium: 0, exclusive: 0, bundle: 0, addon: 0, free: 0, other: 0 };
    allProducts.forEach((product) => {
      const normalized = normalizeProductCategory(product?.category || 'other');
      if (counts[normalized] !== undefined) counts[normalized] += 1;
    });
    return counts;
  }, [allProducts]);
  const activeCategoryLabel = categories.find((cat) => cat.value === selectedCategory)?.label || categories[0].label;
  const selectedSortLabel = sortOptions.find((option) => option.value === sortBy)?.label || sortOptions[0].label;
  const visibleCount = displayedProducts.length;

  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen bg-gray-50 pb-20 text-gray-950 dark:bg-gray-950 dark:text-white">

      <section className="border-b border-gray-200/70 bg-white/75 dark:border-white/10 dark:bg-gray-950/80">
        <div className="mx-auto max-w-7xl px-3 py-4 md:px-6 md:py-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="min-w-0">
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary-200/70 bg-primary-50 px-2.5 py-1 text-[11px] font-black uppercase tracking-[0.16em] text-primary-700 dark:border-primary-400/20 dark:bg-primary-400/10 dark:text-primary-200">
                <Sparkles className="h-3.5 w-3.5" />
                {t('products.catalog_badge', 'Store Catalog')}
              </div>
              <h1 className="text-2xl font-black tracking-normal text-gray-950 dark:text-white md:text-4xl">
                {activeCategoryLabel}
              </h1>
              <p className="mt-1 text-sm font-semibold text-gray-500 dark:text-gray-400">
                {filteredProducts.length} {t('products.items_available', 'items available')} - {t('products.sorted_by', 'Sorted by')} {selectedSortLabel}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
              <div className="rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-white/[0.045]">
                <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('products.showing', 'Showing')}</p>
                <p className="mt-0.5 text-sm font-black text-gray-950 dark:text-white">{visibleCount}/{filteredProducts.length}</p>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-white/10 dark:bg-white/[0.045]">
                <p className="text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('products.total', 'Total')}</p>
                <p className="mt-0.5 text-sm font-black text-gray-950 dark:text-white">{allProducts.length}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sticky Categories & Tools Bar */}
      <div className="sticky top-[58px] z-30 border-b border-gray-200/70 bg-gray-50/90 backdrop-blur-xl dark:border-white/10 dark:bg-gray-950/90 md:top-[68px]">
        <div className="mx-auto max-w-7xl px-3 md:px-6">
          <div className="flex items-center gap-2 overflow-x-auto py-3 no-scrollbar">

            {/* Filter Toggle (Icon only on mobile) */}
            <button
              onClick={() => setSortOpen(!sortOpen)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-gray-200 bg-white text-gray-700 shadow-sm transition hover:border-primary-200 hover:text-primary-700 dark:border-white/10 dark:bg-white/[0.055] dark:text-gray-300 dark:hover:bg-white/[0.09] md:h-auto md:w-auto md:px-3 md:py-2"
            >
              <SlidersHorizontal className="w-5 h-5 md:w-4 md:h-4 md:mr-2" />
              <span className="hidden md:inline text-sm font-black">{t('products.sort')}</span>
            </button>

            {/* Categories - Horizontal Scroll Pills */}
            <div className="flex items-center gap-2 flex-1">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryChange(cat.value)}
                  className={`shrink-0 rounded-2xl border px-3 py-2 text-left transition-all ${selectedCategory === cat.value
                    ? 'border-primary-500 bg-primary-600 text-white shadow-lg shadow-primary-600/15 dark:border-white dark:bg-white dark:text-gray-950 dark:shadow-none'
                    : 'border-gray-200 bg-white text-gray-600 shadow-sm hover:border-primary-200 hover:text-gray-950 dark:border-white/10 dark:bg-white/[0.055] dark:text-gray-300 dark:hover:bg-white/[0.09] dark:hover:text-white'
                    }`}
                >
                  <span className="block text-sm font-black leading-none">{cat.label}</span>
                  <span className={`mt-1 block text-[10px] font-bold leading-none ${selectedCategory === cat.value ? 'text-white/75 dark:text-gray-500' : 'text-gray-400 dark:text-gray-500'}`}>
                    {categoryCounts[cat.value] || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* View Toggle */}
            <div className="hidden shrink-0 rounded-2xl border border-gray-200 bg-white p-1 shadow-sm dark:border-white/10 dark:bg-white/[0.055] md:flex">
              <button
                onClick={() => setViewMode('grid')}
                className={`rounded-xl p-2 transition-all ${viewMode === 'grid' ? 'bg-primary-600 text-white shadow-sm dark:bg-white dark:text-gray-950' : 'text-gray-500 dark:text-gray-400'}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`rounded-xl p-2 transition-all ${viewMode === 'list' ? 'bg-primary-600 text-white shadow-sm dark:bg-white dark:text-gray-950' : 'text-gray-500 dark:text-gray-400'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Sort Dropdown (Absolute) */}
        <AnimatePresence>
          {sortOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setSortOpen(false)} />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute left-3 top-16 z-50 w-64 overflow-hidden rounded-3xl border border-gray-200 bg-white p-2 shadow-xl dark:border-white/10 dark:bg-gray-900 md:left-[max(1.5rem,calc((100vw-80rem)/2+1.5rem))]"
              >
                <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-xs font-black uppercase tracking-wider text-gray-500 dark:border-white/10 dark:text-gray-400">
                  <SortAsc className="h-4 w-4" />
                  {t('products.sort_by')}
                </div>
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className="mt-1 flex w-full items-center justify-between rounded-2xl px-3 py-2.5 text-left text-sm transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.07]"
                  >
                    <span className={sortBy === option.value ? 'text-primary-600 font-black dark:text-primary-300' : 'font-semibold text-gray-700 dark:text-gray-300'}>
                      {option.label}
                    </span>
                    {sortBy === option.value && <Check className="w-4 h-4 text-primary-600" />}
                  </button>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <div className="mx-auto max-w-7xl px-3 pt-4 md:px-6 md:pt-6">
        <div className="flex gap-8 items-start">

          {/* Product Grid - Consistent 12px padding mobile */}
          <div className="flex-1 min-w-0">
            {filteredProducts.length === 0 ? (
              <div className="mx-auto max-w-lg rounded-[2rem] border border-dashed border-gray-200 bg-white px-5 py-12 text-center shadow-sm dark:border-white/10 dark:bg-white/[0.045]">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 text-gray-400 dark:bg-white/[0.06]">
                  <PackageSearch className="h-7 w-7" />
                </div>
                <h3 className="mb-2 text-xl font-black text-gray-950 dark:text-white">{t('products.no_found_title')}</h3>
                <p className="mx-auto mb-6 max-w-sm text-sm font-medium text-gray-500 dark:text-gray-400">
                  {t('products.no_found_subtitle')}
                </p>
                <Button variant="outline" onClick={() => dispatch(setSelectedCategory('all'))}>
                  {t('products.clear_filters')}
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-black text-gray-600 shadow-sm dark:border-white/10 dark:bg-white/[0.045] dark:text-gray-300">
                    <Layers3 className="h-3.5 w-3.5 text-primary-500" />
                    {activeCategoryLabel}
                  </div>
                  <div className="text-xs font-bold text-gray-500 dark:text-gray-400">
                    {visibleCount} {t('products.visible', 'visible')}
                  </div>
                </div>

                <div className={`grid gap-3 md:gap-5 ${viewMode === 'grid'
                  ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'
                  : 'grid-cols-1'
                  }`}>
                  <AnimatePresence mode='popLayout'>
                    {displayedProducts.map((product, index) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        index={index % PRODUCTS_PER_PAGE}
                        viewMode={viewMode}
                        isCompact={true}
                        enableAnimation={scrollPosition === 0}
                        // Force compact mobile style if needed, but ProductCard handles it
                        className="h-full border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-shadow"
                      />
                    ))}
                  </AnimatePresence>
                </div>

                {/* Load More */}
                {hasMoreProducts && (
                  <div className="mt-8 pb-8 text-center md:mt-12">
                    <Button
                      variant="ghost"
                      onClick={handleLoadMore}
                      disabled={loadingMore}
                      className="min-w-[200px] rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-white/[0.045]"
                    >
                      {loadingMore ? t('products.loading_more') : t('products.load_more')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
